import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    createTheme,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { copyObject } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import {
    fetchEatingDisorderInfo,
    fetchEatingDisorderInfoByMemberId,
    saveEatingDisorderInfo,
    setEatingDisorderInfo,
} from '../../../store/eatingDisorderSlice.slice';
import { EatingDisorderModel } from '../../../Models/EatingDisorderModel/EatingDisorderModel.model';
import { NO, YES } from '../../../utils/constants';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Cultural_Considerations_Eating_Disorder_GAF_Scale.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const yesNoList = [{ Answer: YES }, { Answer: NO }];

const EatingDisorder: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const { eatingDisorderInfo } = useSelector((state: RootState) => state.eatingDisorderSlice);
    const isSaving = isSavingEngagementDraft;
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const eatingDisorderInfoIsComplete = (values: EatingDisorderModel) => {
        const isComplete =
            Boolean(values.DiagnosedWithEatingDisorder === NO) ||
            (Boolean(values.DiagnosedWithEatingDisorder === YES) &&
                (Boolean(values.AnorexiaNervoxa) ||
                    Boolean(values.BulimiaNervosa) ||
                    Boolean(values.BingeEatingDisorder) ||
                    Boolean(values.AvoidantRestrictiveFoodIntakeDisorder) ||
                    Boolean(values.OtherSpecifiedFeedingOrEatingDisorder)));
        return isComplete;
    };

    const handleChangeEatingDisorder = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === NO) {
            dispatch(
                setEatingDisorderInfo({
                    ...eatingDisorderInfo,
                    DiagnosedWithEatingDisorder: event.target.value,
                    AnorexiaNervoxa: false,
                    BulimiaNervosa: false,
                    BingeEatingDisorder: false,
                    AvoidantRestrictiveFoodIntakeDisorder: false,
                    OtherSpecifiedFeedingOrEatingDisorder: false,
                    OtherSpecifiedFeedingOrEatingDisorderDescription: '',
                    EatingDisorderComments: '',
                    IsAIDiagnosedWithEatingDisorder: false,
                })
            );
        } else if (event.target.value === YES) {
            dispatch(setEatingDisorderInfo({ ...eatingDisorderInfo, DiagnosedWithEatingDisorder: event.target.value }));
        }
        setIsChanged(true);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setEatingDisorderInfo({ ...eatingDisorderInfo, [event.target.name]: event.target.checked, IsAIDiagnosedWithEatingDisorder: false }));
        setIsChanged(true);
    };

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (eatingDisorderInfo: EatingDisorderModel, isComplete = false) => {
        const copy = copyObject(eatingDisorderInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveEatingDisorderInfo(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveEatingDisorderInfo(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setEatingDisorderInfo(new EatingDisorderModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        if (engagementDraft?.EatingDisorderId) {
            dispatch(fetchEatingDisorderInfo(engagementDraft.EatingDisorderId));
        } else {
            dispatch(fetchEatingDisorderInfoByMemberId(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                <Grid>
                    {isSaving ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} sx={classes.root}>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Typography variant="h6">Eating Disorder</Typography>
                                        <Tooltip title="Eating Disorder Help Document">
                                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                                        </Tooltip>
                                    </Stack>
                                    <hr />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel>{AssessmentQuestions.EatingDisorder_DiagnosedWithEatingDisorder}</FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    {eatingDisorderInfo.IsAIDiagnosedWithEatingDisorder && isAIIcon()}
                                    <RadioGroup
                                        row
                                        name="DiagnosedWithEatingDisorder"
                                        value={eatingDisorderInfo.DiagnosedWithEatingDisorder || ''}
                                        onChange={handleChangeEatingDisorder}
                                    >
                                        {yesNoList.map((type, index) => {
                                            return (
                                                <FormControlLabel key={`answer-label-${index}`} value={type.Answer} control={<Radio />} label={type.Answer} />
                                            );
                                        })}
                                    </RadioGroup>
                                </Grid>

                                <Collapse in={eatingDisorderInfo.DiagnosedWithEatingDisorder === YES} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                                    <Grid size={6} sx={{ ml: 'auto', md: { ml: '' } }}>
                                        <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                            <FormGroup>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={eatingDisorderInfo.AnorexiaNervoxa}
                                                                onChange={handleChange}
                                                                name="AnorexiaNervoxa"
                                                            />
                                                        }
                                                        label="Anorexia Nervosa"
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={eatingDisorderInfo.BulimiaNervosa}
                                                                onChange={handleChange}
                                                                name="BulimiaNervosa"
                                                            />
                                                        }
                                                        label="Bulimia Nervosa"
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={eatingDisorderInfo.BingeEatingDisorder}
                                                                onChange={handleChange}
                                                                name="BingeEatingDisorder"
                                                            />
                                                        }
                                                        label="Binge Eating Disorder"
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={eatingDisorderInfo.AvoidantRestrictiveFoodIntakeDisorder}
                                                                onChange={handleChange}
                                                                name="AvoidantRestrictiveFoodIntakeDisorder"
                                                            />
                                                        }
                                                        label="Avoidant/Restrictive Food Intake Disorder"
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={eatingDisorderInfo.OtherSpecifiedFeedingOrEatingDisorder}
                                                                onChange={handleChange}
                                                                name="OtherSpecifiedFeedingOrEatingDisorder"
                                                            />
                                                        }
                                                        label="Other specified Feeding and Eating Disorder"
                                                    />
                                                    {eatingDisorderInfo.OtherSpecifiedFeedingOrEatingDisorder && (
                                                        <TextField
                                                            label="Please specify"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            margin="normal"
                                                            name="OtherSpecifiedFeedingOrEatingDisorderDescription"
                                                            value={eatingDisorderInfo.OtherSpecifiedFeedingOrEatingDisorderDescription || ''}
                                                            onChange={(e) => {
                                                                dispatch(
                                                                    setEatingDisorderInfo({
                                                                        ...eatingDisorderInfo,
                                                                        OtherSpecifiedFeedingOrEatingDisorderDescription: e.target.value,
                                                                        IsAIDiagnosedWithEatingDisorder: false,
                                                                    })
                                                                );
                                                                setIsChanged(true);
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </FormGroup>
                                        </FormControl>
                                        <TextField
                                            label={AssessmentQuestions.EatingDisorder_EatingDisorderComments}
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            rows={3}
                                            fullWidth
                                            margin="normal"
                                            name="EatingDisorderComments"
                                            value={eatingDisorderInfo.EatingDisorderComments || ''}
                                            onChange={(e) => {
                                                dispatch(
                                                    setEatingDisorderInfo({
                                                        ...eatingDisorderInfo,
                                                        EatingDisorderComments: e.target.value,
                                                        IsAIDiagnosedWithEatingDisorder: false,
                                                    })
                                                );
                                                setIsChanged(true);
                                            }}
                                        />
                                    </Grid>
                                </Collapse>
                            </Grid>

                            <Grid justifyContent="flex-end" className="content-footer">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ marginRight: 2 }}
                                    disabled={isSaving}
                                    onClick={() => dispatch(gotoPreviousMenu())}
                                    startIcon={<ArrowBackIosIcon />}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSaving}
                                    endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                    onClick={handleSubmit}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </>
                    )}

                    {Boolean(eatingDisorderInfoIsComplete(eatingDisorderInfo) && eatingDisorderInfo.CreatedBy && !isChanged) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title={`Eating Disorder`}
                            buttonsDirection="column-reverse"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText={`Eating Disorder Assessment Not Reviewed`}
                            okText={`Agree With Eating Disorder Assessment`}
                            onConfirm={() => handleOnConfirmToNext(eatingDisorderInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message={`Last Eating Disorder assessment completed on ${(() => {
                                const d = toDate(eatingDisorderInfo.UpdatedDate ?? eatingDisorderInfo.CreatedDate);
                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                            })()}.`}
                        />
                    ) : Boolean(eatingDisorderInfoIsComplete(eatingDisorderInfo)) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Eating Disorder"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(eatingDisorderInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            showBottomCenter={true}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message="I confirm and acknowledge that Eating Disorder is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Eating Disorder"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText="Skip and continue"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(eatingDisorderInfo, false)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message={'Are you sure you wish to proceed without completing Eating Disorder assessment?'}
                        />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};
export default EatingDisorder;
